// src/components/shifts/ShiftManagement.js
import React, { useState, useEffect } from 'react';
import { shiftsService } from '../../services/shiftsService';
import { premisesService } from '../../services/premisesService';
import ShiftForm from './ShiftForm';
import StaffAssignmentModal from './StaffAssignmentModal';
import LoadingSpinner from '../common/LoadingSpinner';
import Toast from '../common/Toast';
import Pagination from '../common/Pagination';
import SearchableSelect from '../common/SearchableSelect';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';

const ShiftManagement = () => {
    usePageTitle('Shift Management');
    const { user } = useAuth();
    const [shifts, setShifts] = useState([]);
    const [staffShifts, setStaffShifts] = useState([]);
    const [error, setError] = useState(null);
    const [premises, setPremises] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingShift, setEditingShift] = useState(null);
    const [activeTab, setActiveTab] = useState('shifts');
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedShift, setSelectedShift] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyStaff, setHistoryStaff] = useState(null);
    const [historyRows, setHistoryRows] = useState([]);
    const [allHistoryRows, setAllHistoryRows] = useState([]);
    const [allHistoryLoading, setAllHistoryLoading] = useState(false);
    const [historyShiftFilter, setHistoryShiftFilter] = useState('');
    const [historyShiftOptions, setHistoryShiftOptions] = useState([]);
    const [historySearchQuery, setHistorySearchQuery] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSchedulePremises, setSelectedSchedulePremises] = useState('');
    const [currentWeekStart, setCurrentWeekStart] = useState(() => {
        const today = new Date();
        const day = today.getDay();
        const diff = day === 0 ? -6 : 1 - day; // Adjust to Monday
        const monday = new Date(today);
        monday.setDate(today.getDate() + diff);
        monday.setHours(0, 0, 0, 0);
        return monday;
    });

    // Toast
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    // Filters
    const [filters, setFilters] = useState({
        premises_id: '',
        status: '1'
    });
    const [shiftPagination, setShiftPagination] = useState({
        current_page: 1,
        per_page: 12,
        total: 0,
        total_pages: 1
    });

    const isAdmin = user?.user_type === 'admin' || user?.user_type === 'client_admin' || user?.user_type === 'core_admin' || user?.user_type === 'app_admin';
    const isCoreAdmin = user?.user_type === 'core_admin';
    const isClientOrAppAdmin = user?.user_type === 'client_admin' || user?.user_type === 'app_admin';
    const isStaff = user?.user_type === 'staff';

    // Permission helpers
    const canEditShift = (shift) => {
        if (isCoreAdmin) return true; // Core admin can edit all shifts
        if (isClientOrAppAdmin) {
            // Client/app admin can only edit shifts they created (premises_id != 0)
            return shift.premises_id !== 0 && shift.premises_id !== '0';
        }
        return false;
    };

    const isDefaultShift = (shift) => {
        return shift.premises_id === 0 || shift.premises_id === '0';
    };

    useEffect(() => {
        loadData();
    }, [filters]);

    useEffect(() => {
        if (activeTab === 'shifts') {
            loadShifts();
        }
    }, [shiftPagination.current_page, shiftPagination.per_page]);

    useEffect(() => {
        loadAllShiftHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historyShiftFilter]);

    // Reload shifts when switching to schedule tab to get all shifts
    useEffect(() => {
        if (activeTab === 'schedule') {
            loadShifts();
        }
    }, [activeTab]);

    // Debug: Log shifts and premises data when schedule tab is active
    useEffect(() => {
        if (activeTab === 'schedule') {
            console.log('=== SCHEDULE DEBUG ===');
            console.log('All shifts:', shifts);
            console.log('All premises:', premises);
            console.log('Selected premises ID:', selectedSchedulePremises);
            if (selectedSchedulePremises) {
                const selected = getSelectedPremises();
                console.log('Selected premises data:', selected);
                console.log('Weekly off days:', selected?.weekly_off_days);
            }
        }
    }, [activeTab, shifts, premises, selectedSchedulePremises]);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
    };

    const loadData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                loadShifts(),
                loadStaffShifts(),
                loadPremises(),
                loadAllShiftHistory(),
                loadHistoryShiftOptions()
            ]);
        } catch (err) {
            showToast(err.message || 'Error loading data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadShifts = async () => {
        try {
            // For the schedule view, we need all shifts regardless of premises filter
            // So we'll load shifts without premises_id filter if on schedule tab
            const shiftFilters = activeTab === 'schedule'
                ? { status: filters.status, page: 1, limit: 500 }  // Only apply status filter for schedule
                : {
                    ...filters,
                    page: shiftPagination.current_page,
                    limit: shiftPagination.per_page
                };  // Apply all filters for shifts tab

            const response = await shiftsService.getShifts(shiftFilters);
            setShifts(response.shifts || []);
            if (activeTab !== 'schedule') {
                const pg = response.pagination || {};
                setShiftPagination(prev => ({
                    ...prev,
                    current_page: Number(pg.current_page || prev.current_page || 1),
                    per_page: Number(pg.per_page || prev.per_page || 12),
                    total: Number(pg.total || 0),
                    total_pages: Number(pg.total_pages || 1)
                }));
            }
        } catch (err) {
            console.error('Error loading shifts:', err);
        }
    };

    const loadStaffShifts = async () => {
        try {
            const response = await shiftsService.getStaffShifts(filters);
            setStaffShifts(response.assignments || response.staff_shifts || []);
        } catch (err) {
            console.error('Error loading staff shifts:', err);
        }
    };

    const loadAllShiftHistory = async () => {
        setAllHistoryLoading(true);
        try {
            const params = {
                premises_id: filters.premises_id,
                status: filters.status
            };
            if (historyShiftFilter) {
                params.shift_id = historyShiftFilter;
            }
            const response = await shiftsService.getAllShiftHistory(params);
            setAllHistoryRows(response?.history || []);
        } catch (err) {
            console.error('Error loading shift history:', err);
        } finally {
            setAllHistoryLoading(false);
        }
    };

    // Dedicated, unpaginated shift list for the History tab's filter dropdown.
    // Reusing the `shifts` state here would be wrong - that list is paginated
    // (12 per page) for the Shifts tab's grid, so the dropdown would silently
    // only offer whichever page the Shifts tab last loaded, making most
    // shifts impossible to filter by.
    const loadHistoryShiftOptions = async () => {
        try {
            const response = await shiftsService.getShifts({ status: '1', page: 1, limit: 500 });
            setHistoryShiftOptions(response.shifts || []);
        } catch (err) {
            console.error('Error loading shift options:', err);
        }
    };

    const loadPremises = async () => {
        if (isAdmin) {
            try {
                const response = await premisesService.getPremisesComplete({}, isCoreAdmin);
                setPremises(response.premises || []);
            } catch (err) {
                console.error('Error loading premises:', err);
            }
        }
    };

    const handleCreateShift = () => {
        setEditingShift(null);
        setShowForm(true);
    };

    const handleEditShift = (shift) => {
        setEditingShift(shift);
        setShowForm(true);
    };

    const handleFormSubmit = async (formData) => {
        try {
            if (editingShift) {
                await shiftsService.updateShift(editingShift.id, formData);
                showToast('Shift updated successfully', 'success');
            } else {
                await shiftsService.createShift(formData);
                showToast('Shift created successfully', 'success');
            }

            await loadData();
            setShowForm(false);
            setEditingShift(null);
        } catch (err) {
            throw err; // Let the form handle the error
        }
    };

    const handleAssignStaff = (shift) => {
        setSelectedShift(shift);
        setShowAssignModal(true);
    };

    const handleRemoveStaffAssignment = async (assignmentId) => {
        if (!window.confirm('Are you sure you want to remove this staff assignment?')) {
            return;
        }

        try {
            await shiftsService.removeStaffFromShift(assignmentId);
            await loadData();
        } catch (err) {
            setError(err.message);
        }
    };

    const openShiftHistory = async (staffPk, staffName, staffCode) => {
        if (!staffPk) return;
        setShowHistoryModal(true);
        setHistoryLoading(true);
        setHistoryStaff({ name: staffName, staff_id: staffCode });
        try {
            const data = await shiftsService.getStaffShiftHistory(staffPk);
            setHistoryRows(data?.history || []);
            if (data?.staff) {
                setHistoryStaff({ name: data.staff.name, staff_id: data.staff.staff_id });
            }
        } catch (err) {
            setError(err.message);
            setHistoryRows([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const closeShiftHistory = () => {
        setShowHistoryModal(false);
        setHistoryStaff(null);
        setHistoryRows([]);
    };

    const csvEscape = (value) => {
        const str = value === null || value === undefined ? '' : String(value);
        return `"${str.replace(/"/g, '""')}"`;
    };

    const handleExportHistoryCsv = () => {
        if (!filteredHistoryRows.length) return;

        const headers = ['Staff Name', 'Staff ID', 'Shift', 'Premises', 'Assigned On', 'Ended On', 'Status'];
        const rows = filteredHistoryRows.map((row) => [
            row.staff_name,
            row.staff_code,
            row.shift_name,
            row.premises_name || 'All Premises',
            row.effective_from ? new Date(row.effective_from).toLocaleDateString() : 'N/A',
            row.effective_to ? new Date(row.effective_to).toLocaleDateString() : (Number(row.is_active) === 1 ? 'Active' : 'N/A'),
            Number(row.is_active) === 1 ? 'Active' : 'Ended'
        ]);

        const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const fileDate = new Date().toISOString().split('T')[0];
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `shift-history-${fileDate}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setShiftPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    const clearFilters = () => {
        setFilters({
            premises_id: '',
            status: '1'
        });
        setShiftPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        try {
            const time = new Date(`2000-01-01T${timeString}`);
            return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
            return timeString;
        }
    };

    const getShiftDuration = (startTime, endTime) => {
        if (!startTime || !endTime) return '';

        try {
            const start = new Date(`2000-01-01T${startTime}`);
            const end = new Date(`2000-01-01T${endTime}`);

            if (end < start) {
                // Handle overnight shifts
                end.setDate(end.getDate() + 1);
            }

            const diffMs = end - start;
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

            return `${diffHours}h ${diffMinutes}m`;
        } catch {
            return '';
        }
    };

    // Get selected premises for schedule
    const getSelectedPremises = () => {
        if (!selectedSchedulePremises) return null;
        // Compare as strings since the API returns id as string
        return premises.find(p => String(p.id) === String(selectedSchedulePremises));
    };

    // Check if a day is a working day for the selected premises
    const isWorkingDay = (dayName) => {
        const selectedPremises = getSelectedPremises();
        // If no premises is selected, all days are working days
        if (!selectedPremises) return true;

        // If premises has no weekly_off_days set, all days are working days
        if (!selectedPremises.weekly_off_days || selectedPremises.weekly_off_days.trim() === '') {
            return true;
        }

        // weekly_off_days is a comma-separated string like "Saturday,Sunday"
        const offDays = selectedPremises.weekly_off_days.split(',').map(d => d.trim());
        return !offDays.includes(dayName);
    };

    // Get date for a specific day in the current week
    const getDateForDay = (dayIndex) => {
        const date = new Date(currentWeekStart);
        date.setDate(currentWeekStart.getDate() + dayIndex);
        return date;
    };

    // Format date as "MMM DD"
    const formatDate = (date) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${String(date.getDate()).padStart(2, '0')}`;
    };

    // Navigate to previous/next week
    const navigateWeek = (direction) => {
        const newWeekStart = new Date(currentWeekStart);
        newWeekStart.setDate(currentWeekStart.getDate() + (direction * 7));
        setCurrentWeekStart(newWeekStart);
    };

    // Filter staff shifts based on search query
    const filteredStaffShifts = staffShifts.filter(assignment => {
        if (!searchQuery) return true;

        const query = searchQuery.toLowerCase();
        return (
            assignment.staff_name?.toLowerCase().includes(query) ||
            assignment.staff_id?.toLowerCase().includes(query) ||
            assignment.shift_name?.toLowerCase().includes(query) ||
            assignment.premises_name?.toLowerCase().includes(query) ||
            assignment.phone_number?.toLowerCase().includes(query)
        );
    });

    // Filter shift history based on staff-name search
    const filteredHistoryRows = allHistoryRows.filter(row => {
        if (!historySearchQuery) return true;

        const query = historySearchQuery.toLowerCase();
        return (
            row.staff_name?.toLowerCase().includes(query) ||
            row.staff_code?.toLowerCase().includes(query) ||
            row.shift_name?.toLowerCase().includes(query) ||
            row.premises_name?.toLowerCase().includes(query)
        );
    });

    const premisesFilterOptions = premises.map((premise) => {
        const orgName = premise.organization_name || premise.org_name || '';
        return {
            id: String(premise.id),
            name: orgName ? `${premise.premises_name} (${orgName})` : premise.premises_name
        };
    });

    return (
        <div className="shift-management">
            <div className="container">
                {/* Header */}
                <div className="page-header">
                    <div className="header-left">
                        <h1>Shift Management</h1>
                        <p>Manage work shifts and staff scheduling</p>
                    </div>
                    {isAdmin && (
                        <div className="header-right">
                            <button
                                className="btn btn-primary"
                                onClick={handleCreateShift}
                            >
                                <i className="fas fa-plus"></i>
                                Create New Shift
                            </button>
                        </div>
                    )}
                </div>

                {/* Tab Navigation */}
                <div className="tab-navigation">
                    <button
                        className={`tab-btn ${activeTab === 'shifts' ? 'active' : ''}`}
                        onClick={() => setActiveTab('shifts')}
                    >
                        <i className="fas fa-clock"></i>
                        Shifts
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'assignments' ? 'active' : ''}`}
                        onClick={() => setActiveTab('assignments')}
                    >
                        <i className="fas fa-users"></i>
                        Staff Assignments
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'schedule' ? 'active' : ''}`}
                        onClick={() => setActiveTab('schedule')}
                    >
                        <i className="fas fa-calendar-week"></i>
                        Weekly Schedule
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        <i className="fas fa-history"></i>
                        Shift History
                    </button>
                </div>

                {/* Filters */}
                <div className="filters-section">
                    <div className="filters-header">
                        <h3>
                            <i className="fas fa-filter"></i>
                            Filters
                        </h3>
                        <button className="btn btn-secondary btn-sm" onClick={clearFilters}>
                            <i className="fas fa-eraser"></i>
                            Clear Filters
                        </button>
                    </div>

                    <div className="filters-grid">
                        {isAdmin && (
                            <div className="filter-group">
                                <label>Premises</label>
                                <SearchableSelect
                                    options={premisesFilterOptions}
                                    value={String(filters.premises_id || '')}
                                    onChange={(value) => handleFilterChange('premises_id', String(value || ''))}
                                    placeholder="All Premises"
                                    displayKey="name"
                                    valueKey="id"
                                    searchPlaceholder="Search premises or organization..."
                                    className="custom-select"
                                />
                            </div>
                        )}

                        <div className="filter-group">
                            <label>Status</label>
                            <select
                                className="form-input"
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <LoadingSpinner message="Loading shift data..." />
                ) : (
                    <>
                        {/* Shifts Tab */}
                        {activeTab === 'shifts' && (
                            <div className="shifts-section">
                                <div className="shifts-grid">
                                    {shifts.map(shift => (
                                        <div key={shift.id} className={`shift-card ${isDefaultShift(shift) ? 'default-shift' : ''}`}>
                                            <div className="shift-header">
                                                <div className="shift-title">
                                                    <h3>{shift.shift_name}</h3>
                                                    {isDefaultShift(shift) && (
                                                        <span className="default-badge">
                                                            <i className="fas fa-star"></i>
                                                            Default Shift
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="shift-status">
                                                    <span className={`status-badge ${shift.status === '1' || shift.status === 1 ? 'active' : 'inactive'}`}>
                                                        {shift.status === '1' || shift.status === 1 ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="shift-details">
                                                <div className="detail-row">
                                                    <div className="detail-item">
                                                        <i className="fas fa-clock"></i>
                                                        <span>
                                                            {formatTime(shift.premises_start_time)} - {formatTime(shift.premises_end_time)}
                                                        </span>
                                                    </div>
                                                    <div className="shift-duration">
                                                        {getShiftDuration(shift.premises_start_time, shift.premises_end_time)}
                                                    </div>
                                                </div>

                                                {shift.premises_name && (
                                                    <div className="detail-row">
                                                        <div className="detail-item">
                                                            <i className="fas fa-building"></i>
                                                            <span>{shift.premises_name}</span>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="detail-row">
                                                    <div className="detail-item">
                                                        <i className="fas fa-users"></i>
                                                        <span>{shift.assigned_staff || 0} staff assigned</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {isAdmin && (
                                                <div className="shift-actions">
                                                    {/* Only show assign staff for custom shifts (not default ones) */}
                                                    {!isDefaultShift(shift) && (
                                                        <button
                                                            className="btn btn-action btn-secondary"
                                                            onClick={() => handleAssignStaff(shift)}
                                                        >
                                                            <i className="fas fa-user-plus"></i>
                                                            Assign Staff
                                                        </button>
                                                    )}
                                                    {canEditShift(shift) && (
                                                        <button
                                                            className="btn btn-action btn-primary"
                                                            onClick={() => handleEditShift(shift)}
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                            Edit
                                                        </button>
                                                    )}
                                                    {!canEditShift(shift) && isDefaultShift(shift) && (
                                                        <span className="readonly-note">
                                                            <i className="fas fa-info-circle"></i>
                                                            All staff are on this shift by default
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {shifts.length === 0 && (
                                    <div className="empty-state">
                                        <i className="fas fa-clock"></i>
                                        <h3>No Shifts Found</h3>
                                        <p>
                                            {isAdmin
                                                ? 'Create shifts to start managing staff schedules'
                                                : 'No shifts have been created yet'
                                            }
                                        </p>
                                        {isAdmin && (
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleCreateShift}
                                            >
                                                Create First Shift
                                            </button>
                                        )}
                                    </div>
                                )}

                                {shifts.length > 0 && shiftPagination.total_pages > 1 && (
                                    <div className="shifts-pagination-wrap">
                                        <Pagination
                                            currentPage={shiftPagination.current_page}
                                            totalPages={shiftPagination.total_pages}
                                            onPageChange={(page) => setShiftPagination(prev => ({ ...prev, current_page: page }))}
                                            totalItems={shiftPagination.total}
                                            itemsPerPage={shiftPagination.per_page}
                                            showInfo={true}
                                            size="small"
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Staff Assignments Tab */}
                        {activeTab === 'assignments' && (
                            <div className="assignments-section">
                                {/* Search Bar */}
                                <div className="search-section">
                                    <div className="search-input-wrapper">
                                        <i className="fas fa-search search-icon"></i>
                                        <input
                                            type="text"
                                            className="search-input"
                                            placeholder="Search by staff name, ID, shift, premises, or phone..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        {searchQuery && (
                                            <button
                                                className="clear-search-btn"
                                                onClick={() => setSearchQuery('')}
                                                title="Clear search"
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        )}
                                    </div>
                                    {searchQuery && (
                                        <div className="search-results-info">
                                            Found {filteredStaffShifts.length} of {staffShifts.length} assignments
                                        </div>
                                    )}
                                </div>

                                <div className="assignments-table-container">
                                    <table className="assignments-table">
                                        <thead>
                                            <tr>
                                                <th>Staff Name</th>
                                                <th>Shift</th>
                                                <th>Premises</th>
                                                <th>Time</th>
                                                <th>Effective From</th>
                                                {isAdmin && <th>Actions</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredStaffShifts.map((assignment) => (
                                                <tr key={assignment.id}>
                                                    <td>
                                                        <div className="staff-info">
                                                            <div className="staff-name">{assignment.staff_name}</div>
                                                            <div className="staff-id">ID: {assignment.staff_id}</div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="shift-info">
                                                            <span className="shift-name">{assignment.shift_name}</span>
                                                            {(assignment.premises_id === 0 || assignment.premises_id === '0') && (
                                                                <span className="default-badge-mini">
                                                                    <i className="fas fa-star"></i>
                                                                    Default
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>{assignment.premises_name || 'All Premises'}</td>
                                                    <td>
                                                        {formatTime(assignment.premises_start_time || assignment.start_time)} - {formatTime(assignment.premises_end_time || assignment.end_time)}
                                                    </td>
                                                    <td>
                                                        {assignment.effective_from ? new Date(assignment.effective_from).toLocaleDateString() : 'N/A'}
                                                    </td>
                                                    {isAdmin && (
                                                        <td>
                                                            <div className="assignment-actions">
                                                                <button
                                                                    className="btn btn-sm btn-secondary"
                                                                    onClick={() => openShiftHistory(assignment.staff_pk, assignment.staff_name, assignment.staff_id)}
                                                                    title="View Shift History"
                                                                >
                                                                    <i className="fas fa-history"></i>
                                                                </button>
                                                                {Number(assignment.can_remove ?? 1) === 1 && (
                                                                    <button
                                                                        className="btn btn-sm btn-danger"
                                                                        onClick={() => handleRemoveStaffAssignment(assignment.id)}
                                                                        title="Remove Assignment"
                                                                    >
                                                                        <i className="fas fa-user-times"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {filteredStaffShifts.length === 0 && (
                                        <div className="empty-assignments">
                                            <i className="fas fa-user-clock"></i>
                                            <h3>{searchQuery ? 'No Matching Assignments' : 'No Staff Assignments'}</h3>
                                            <p>{searchQuery ? 'Try adjusting your search query' : 'No staff have been assigned to shifts yet'}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Weekly Schedule Tab */}
                        {activeTab === 'schedule' && (
                            <div className="schedule-section">
                                <div className="schedule-container">
                                    <div className="schedule-header">
                                        <div className="schedule-header-left">
                                            <h3>Weekly Schedule Overview</h3>
                                            {isAdmin && (
                                                <div className="premises-selector">
                                                    <label>Select Premises:</label>
                                                    <select
                                                        className="form-input"
                                                        value={selectedSchedulePremises}
                                                        onChange={(e) => setSelectedSchedulePremises(e.target.value)}
                                                    >
                                                        <option value="">All Premises</option>
                                                        {premises.map(premise => (
                                                            <option key={premise.id} value={premise.id}>
                                                                {premise.premises_name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                        <div className="schedule-controls">
                                            <button className="btn btn-secondary" onClick={() => navigateWeek(-1)}>
                                                <i className="fas fa-chevron-left"></i>
                                                Previous Week
                                            </button>
                                            <span className="current-week">
                                                {formatDate(currentWeekStart)} - {formatDate(getDateForDay(6))}
                                            </span>
                                            <button className="btn btn-secondary" onClick={() => navigateWeek(1)}>
                                                Next Week
                                                <i className="fas fa-chevron-right"></i>
                                            </button>
                                        </div>
                                    </div>

                                    {selectedSchedulePremises && getSelectedPremises() && (
                                        <div className="premises-info-banner">
                                            <i className="fas fa-info-circle"></i>
                                            <span>
                                                <strong>{getSelectedPremises().premises_name}</strong>
                                                {getSelectedPremises().weekly_off_days && getSelectedPremises().weekly_off_days.trim() !== '' ? (
                                                    <span> - Weekly Off Days: {getSelectedPremises().weekly_off_days}</span>
                                                ) : (
                                                    <span> - No weekly off days configured (All days are working days)</span>
                                                )}
                                            </span>
                                        </div>
                                    )}                                 

                                    <div className="schedule-grid">
                                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, dayIndex) => {
                                            const isWorking = isWorkingDay(day);
                                            const dayDate = getDateForDay(dayIndex);

                                            const filteredShifts = shifts.filter(shift => {
                                                // Show active shifts only
                                                if (shift.status !== '1' && shift.status !== 1) return false;

                                                // If a premises is selected
                                                if (selectedSchedulePremises) {
                                                    const selectedPremisesId = parseInt(selectedSchedulePremises);
                                                    const shiftPremisesId = parseInt(shift.premises_id);

                                                    // Show shifts for the selected premises OR default shifts (premises_id = 0)
                                                    return shiftPremisesId === selectedPremisesId || shiftPremisesId === 0;
                                                }

                                                return true;
                                            });

                                            return (
                                                <div key={day} className={`day-column ${!isWorking ? 'off-day' : ''}`}>
                                                    <div className="day-header">
                                                        <h4>{day}</h4>
                                                        <span className="day-date">{formatDate(dayDate)}</span>
                                                        {!isWorking && (
                                                            <span className="off-day-badge">
                                                                <i className="fas fa-calendar-times"></i>
                                                                OFF
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="day-shifts">
                                                        {isWorking ? (
                                                            filteredShifts.length > 0 ? (
                                                                filteredShifts.map(shift => (
                                                                    <div key={shift.id} className="schedule-shift-item">
                                                                        <div className="shift-time">
                                                                            {formatTime(shift.premises_start_time)} - {formatTime(shift.premises_end_time)}
                                                                        </div>
                                                                        <div className="shift-name">
                                                                            {shift.shift_name}
                                                                            {isDefaultShift(shift) && <i className="fas fa-star" style={{ marginLeft: '5px', fontSize: '0.7rem', color: '#fbbf24' }}></i>}
                                                                        </div>
                                                                        <div className="shift-staff-count">
                                                                            {shift.assigned_staff || (isDefaultShift(shift) ? 'All' : '0')} staff
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="no-shifts-message">
                                                                    <i className="fas fa-clock"></i>
                                                                    <span>No shifts scheduled</span>
                                                                </div>
                                                            )
                                                        ) : (
                                                            <div className="off-day-message">
                                                                <i className="fas fa-moon"></i>
                                                                <span>Weekly Off</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Shift History Tab */}
                        {activeTab === 'history' && (
                            <div className="assignments-section">
                                <div className="search-section">
                                    <div className="search-input-wrapper">
                                        <i className="fas fa-search search-icon"></i>
                                        <input
                                            type="text"
                                            className="search-input"
                                            placeholder="Search by staff name, ID, shift, or premises..."
                                            value={historySearchQuery}
                                            onChange={(e) => setHistorySearchQuery(e.target.value)}
                                        />
                                        {historySearchQuery && (
                                            <button
                                                className="clear-search-btn"
                                                onClick={() => setHistorySearchQuery('')}
                                                title="Clear search"
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        )}
                                    </div>
                                    {historySearchQuery && (
                                        <div className="search-results-info">
                                            Found {filteredHistoryRows.length} of {allHistoryRows.length} records
                                        </div>
                                    )}
                                </div>

                                <div className="search-section history-filter-row">
                                    <div className="filter-group history-shift-filter">
                                        <label>Shift</label>
                                        <select
                                            className="form-input"
                                            value={historyShiftFilter}
                                            onChange={(e) => setHistoryShiftFilter(e.target.value)}
                                        >
                                            <option value="">All Shifts</option>
                                            {historyShiftOptions.map((shift) => (
                                                <option key={shift.id} value={shift.id}>
                                                    {shift.shift_name}{shift.premises_name ? ` — ${shift.premises_name}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <button
                                        className="btn btn-secondary"
                                        onClick={handleExportHistoryCsv}
                                        disabled={filteredHistoryRows.length === 0}
                                    >
                                        <i className="fas fa-file-csv"></i>
                                        Export CSV
                                    </button>
                                </div>

                                {allHistoryLoading ? (
                                    <LoadingSpinner message="Loading shift history..." />
                                ) : (
                                    <div className="assignments-table-container">
                                        <table className="assignments-table">
                                            <thead>
                                                <tr>
                                                    <th>Staff Name</th>
                                                    <th>Shift</th>
                                                    <th>Premises</th>
                                                    <th>Assigned On</th>
                                                    <th>Ended On</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredHistoryRows.map((row) => (
                                                    <tr key={row.id}>
                                                        <td>
                                                            <div className="staff-info">
                                                                <div className="staff-name">{row.staff_name}</div>
                                                                <div className="staff-id">ID: {row.staff_code}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="shift-info">
                                                                <span className="shift-name">{row.shift_name}</span>
                                                                {(row.premises_id === 0 || row.premises_id === '0') && (
                                                                    <span className="default-badge-mini">
                                                                        <i className="fas fa-star"></i>
                                                                        Default
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>{row.premises_name || 'All Premises'}</td>
                                                        <td>{row.effective_from ? new Date(row.effective_from).toLocaleDateString() : 'N/A'}</td>
                                                        <td>{row.effective_to ? new Date(row.effective_to).toLocaleDateString() : (Number(row.is_active) === 1 ? '—' : 'N/A')}</td>
                                                        <td>
                                                            {Number(row.is_active) === 1 ? (
                                                                <span className="status-badge-active">Active</span>
                                                            ) : (
                                                                <span className="status-badge-ended">Ended</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {filteredHistoryRows.length === 0 && (
                                            <div className="empty-assignments">
                                                <i className="fas fa-history"></i>
                                                <h3>No Shift History</h3>
                                                <p>{historySearchQuery ? 'No records match your search.' : 'No shift assignments found for the selected filters.'}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* Shift Form Modal */}
                {showForm && (
                    <ShiftForm
                        shift={editingShift}
                        premises={premises}
                        user={user}
                        onSubmit={handleFormSubmit}
                        onClose={() => {
                            setShowForm(false);
                            setEditingShift(null);
                        }}
                    />
                )}

                {/* Staff Assignment Modal */}
                {showAssignModal && selectedShift && (
                    <StaffAssignmentModal
                        shift={selectedShift}
                        onClose={() => {
                            setShowAssignModal(false);
                            setSelectedShift(null);
                        }}
                        onSuccess={(message) => {
                            loadData(); // Reload data to show updated assignments
                            if (message) {
                                showToast(message, 'success');
                            }
                        }}
                        onError={(message) => {
                            showToast(message, 'error');
                        }}
                    />
                )}

                {/* Shift History Modal */}
                {showHistoryModal && (
                    <div className="modal-overlay" onClick={closeShiftHistory}>
                        <div className="assign-modal history-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>
                                    Shift History{historyStaff?.name ? ` — ${historyStaff.name}` : ''}
                                    {historyStaff?.staff_id ? <span className="history-staff-code"> (ID: {historyStaff.staff_id})</span> : null}
                                </h3>
                                <button className="close-btn" onClick={closeShiftHistory}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="modal-body">
                                {historyLoading ? (
                                    <LoadingSpinner />
                                ) : historyRows.length === 0 ? (
                                    <div className="empty-state">
                                        <i className="fas fa-history"></i>
                                        <h3>No Shift History</h3>
                                        <p>This staff member has no recorded shift assignments yet.</p>
                                    </div>
                                ) : (
                                    <div className="assignments-table-container">
                                        <table className="assignments-table">
                                            <thead>
                                                <tr>
                                                    <th>Shift</th>
                                                    <th>Premises</th>
                                                    <th>Assigned On</th>
                                                    <th>Ended On</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {historyRows.map((row) => (
                                                    <tr key={row.id}>
                                                        <td>
                                                            <div className="shift-info">
                                                                <span className="shift-name">{row.shift_name}</span>
                                                                {(row.premises_id === 0 || row.premises_id === '0') && (
                                                                    <span className="default-badge-mini">
                                                                        <i className="fas fa-star"></i>
                                                                        Default
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>{row.premises_name || 'All Premises'}</td>
                                                        <td>{row.effective_from ? new Date(row.effective_from).toLocaleDateString() : 'N/A'}</td>
                                                        <td>{row.effective_to ? new Date(row.effective_to).toLocaleDateString() : (Number(row.is_active) === 1 ? '—' : 'N/A')}</td>
                                                        <td>
                                                            {Number(row.is_active) === 1 ? (
                                                                <span className="status-badge-active">Active</span>
                                                            ) : (
                                                                <span className="status-badge-ended">Ended</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Toast */}
                <Toast
                    show={toast.show}
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ show: false, message: '', type: 'success' })}
                />
            </div>

            <style jsx>{`
                .shift-management {
                    min-height: 100vh;
                    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                    padding: 2rem 0;
                }

                .container {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 1.5rem;
                }

                .page-header {
                    background: white;
                    padding: 2rem;
                    border-radius: 20px;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
                    margin-bottom: 2rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-left: 5px solid #6366f1;
                }

                .header-left h1 {
                    color: #2d3748;
                    margin-bottom: 0.5rem;
                    font-size: 2rem;
                    font-weight: 700;
                }

                .header-left p {
                    color: #718096;
                    margin: 0;
                    font-size: 1rem;
                }

                .header-right {
                    display: flex;
                    gap: 1rem;
                    align-items: center;
                }

                .tab-navigation {
                    display: flex;
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                    margin-bottom: var(--spacing-xl);
                }

                .tab-btn {
                    flex: 1;
                    padding: var(--spacing-lg);
                    border: none;
                    background: var(--white);
                    color: var(--text-light);
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: var(--spacing-sm);
                    font-size: 0.9rem;
                    font-weight: 500;
                    transition: all 0.3s ease;
                    border-bottom: 3px solid transparent;
                }

                .tab-btn:hover {
                    color: var(--admin-primary);
                    background: rgba(var(--admin-primary-rgb), 0.05);
                }

                .tab-btn.active {
                    color: var(--admin-primary);
                    background: rgba(var(--admin-primary-rgb), 0.1);
                    border-bottom-color: var(--admin-primary);
                }

                .filters-section {
                    background: white;
                    padding: 2rem;
                    border-radius: 20px;
                    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
                    margin-bottom: 2rem;
                }

                .filters-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                    padding-bottom: 1rem;
                    border-bottom: 2px solid #e2e8f0;
                }

                .filters-header h3 {
                    font-size: 1.3rem;
                    font-weight: 600;
                    color: #2d3748;
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 0.7rem;
                }

                .filters-header h3 i {
                    color: #6366f1;
                }

                .filters-grid {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 1.25rem 1.5rem;
                    align-items: flex-end;
                }

                .filter-group {
                    display: flex;
                    flex-direction: column;
                    gap: 0.55rem;
                    flex: 1 1 260px;
                    min-width: 220px;
                    max-width: 380px;
                }

                .filter-group label {
                    font-weight: 600;
                    color: #2d3748;
                    font-size: 0.9rem;
                    margin-bottom: 0.15rem;
                }

                .form-input {
                    padding: 0.75rem 1rem;
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    font-size: 0.95rem;
                    transition: all 0.3s ease;
                    background: white;
                }

                .form-input:focus {
                    outline: none;
                    border-color: #6366f1;
                    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
                }

                .shifts-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
                    gap: var(--spacing-xl);
                }

                .shifts-pagination-wrap {
                    margin-top: 1.25rem;
                    background: #fff;
                    padding: 0.75rem 1rem;
                    border-radius: 12px;
                    box-shadow: var(--shadow-sm);
                }

                .shift-card {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                    overflow: hidden;
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }

                .shift-card:hover {
                    transform: translateY(-4px);
                    box-shadow: var(--shadow-lg);
                }

                .shift-header {
                    background: var(--light-color);
                    padding: var(--spacing-lg);
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                }

                .shift-title h3 {
                    color: var(--text-dark);
                    margin: 0 0 var(--spacing-sm) 0;
                    font-size: 1.2rem;
                }

                .default-badge {
                    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
                    color: white;
                    padding: 0.4rem 0.8rem;
                    border-radius: 8px;
                    font-size: 0.7rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.3rem;
                    box-shadow: 0 2px 8px rgba(251, 191, 36, 0.3);
                }

                .default-badge i {
                    font-size: 0.65rem;
                }

                .default-badge-mini {
                    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
                    color: white;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-size: 0.65rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.2rem;
                }

                .default-badge-mini i {
                    font-size: 0.6rem;
                }

                .shift-card.default-shift {
                    border-left: 4px solid #fbbf24;
                }

                .readonly-note {
                    color: #718096;
                    font-size: 0.8rem;
                    font-style: italic;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .readonly-note i {
                    color: #6366f1;
                }

                .status-badge {
                    padding: var(--spacing-xs) var(--spacing-sm);
                    border-radius: var(--radius-sm);
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                }

                .status-badge.active {
                    background: rgba(34, 197, 94, 0.1);
                    color: #059669;
                }

                .status-badge.inactive {
                    background: rgba(239, 68, 68, 0.1);
                    color: #dc2626;
                }

                .shift-details {
                    padding: var(--spacing-lg);
                }

                .detail-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: var(--spacing-md);
                }

                .detail-row:last-child {
                    margin-bottom: 0;
                }

                .detail-item {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    color: var(--text-dark);
                    font-size: 0.9rem;
                }

                .detail-item i {
                    color: var(--admin-primary);
                    width: 16px;
                }

                .shift-duration {
                    background: rgba(var(--admin-primary-rgb), 0.1);
                    color: var(--admin-primary);
                    padding: var(--spacing-xs) var(--spacing-sm);
                    border-radius: var(--radius-sm);
                    font-size: 0.8rem;
                    font-weight: 600;
                }

                .shift-description {
                    margin-top: var(--spacing-md);
                    padding-top: var(--spacing-md);
                    border-top: 1px solid rgba(0, 0, 0, 0.05);
                }

                .shift-description p {
                    color: var(--text-light);
                    font-size: 0.85rem;
                    margin: 0;
                    line-height: 1.4;
                }

                .shift-actions {
                    padding: var(--spacing-lg);
                    border-top: 1px solid rgba(0, 0, 0, 0.05);
                    display: flex;
                    gap: var(--spacing-sm);
                    flex-wrap: wrap;
                }

                .assignments-table-container {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                    overflow: hidden;
                }

                .assignments-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .assignments-table th,
                .assignments-table td {
                    padding: var(--spacing-md);
                    text-align: left;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                    font-size: 0.9rem;
                }

                .assignments-table th {
                    background: var(--light-color);
                    font-weight: 600;
                    color: var(--text-dark);
                }

                .assignments-table tr:hover {
                    background: rgba(var(--admin-primary-rgb), 0.02);
                }

                .staff-info {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-xs);
                }

                .staff-name {
                    font-weight: 600;
                    color: var(--text-dark);
                }

                .staff-id {
                    font-size: 0.75rem;
                    color: var(--text-light);
                }

                .shift-info {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                }

                .shift-name {
                    font-weight: 500;
                }

                .working-days {
                    display: flex;
                    gap: var(--spacing-xs);
                    flex-wrap: wrap;
                }

                .day-badge {
                    background: rgba(var(--admin-primary-rgb), 0.1);
                    color: var(--admin-primary);
                    padding: 2px var(--spacing-xs);
                    border-radius: 4px;
                    font-size: 0.7rem;
                    font-weight: 600;
                }

                .search-section {
                    background: var(--white);
                    padding: var(--spacing-lg);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                    margin-bottom: var(--spacing-lg);
                }

                .history-filter-row {
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-between;
                    gap: var(--spacing-md);
                    flex-wrap: wrap;
                }

                .history-shift-filter {
                    min-width: 220px;
                    margin-bottom: 0;
                }

                .search-input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .search-input-wrapper .search-icon {
                    position: absolute;
                    left: 1rem;
                    color: var(--text-light);
                    font-size: 0.9rem;
                }

                .search-input {
                    width: 100%;
                    padding: 0.75rem 1rem 0.75rem 2.5rem;
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    font-size: 0.95rem;
                    transition: all 0.3s ease;
                    background: white;
                }

                .search-input:focus {
                    outline: none;
                    border-color: var(--admin-primary);
                    box-shadow: 0 0 0 3px rgba(var(--admin-primary-rgb), 0.1);
                }

                .clear-search-btn {
                    position: absolute;
                    right: 1rem;
                    background: none;
                    border: none;
                    color: var(--text-light);
                    cursor: pointer;
                    padding: 0.5rem;
                    transition: color 0.2s ease;
                }

                .clear-search-btn:hover {
                    color: var(--admin-primary);
                }

                .search-results-info {
                    margin-top: var(--spacing-md);
                    padding: var(--spacing-sm) var(--spacing-md);
                    background: rgba(var(--admin-primary-rgb), 0.05);
                    border-radius: var(--radius-md);
                    color: var(--admin-primary);
                    font-size: 0.9rem;
                    font-weight: 500;
                    text-align: center;
                }

                .empty-assignments {
                    text-align: center;
                    padding: var(--spacing-xl) * 2;
                    color: var(--text-light);
                }

                .empty-assignments i {
                    font-size: 3rem;
                    margin-bottom: var(--spacing-lg);
                    color: var(--admin-primary);
                    opacity: 0.5;
                }

                .schedule-container {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                    overflow: hidden;
                }

                .schedule-header {
                    background: var(--light-color);
                    padding: var(--spacing-xl);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: var(--spacing-lg);
                }

                .schedule-header-left {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-md);
                }

                .schedule-header h3 {
                    margin: 0;
                    color: var(--text-dark);
                }

                .premises-selector {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                }

                .premises-selector label {
                    font-weight: 600;
                    color: var(--text-dark);
                    font-size: 0.9rem;
                    white-space: nowrap;
                }

                .premises-selector select {
                    min-width: 200px;
                }

                .premises-info-banner {
                    background: rgba(var(--admin-primary-rgb), 0.1);
                    border-left: 4px solid var(--admin-primary);
                    padding: var(--spacing-md) var(--spacing-lg);
                    border-radius: var(--radius-md);
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    margin: 0 var(--spacing-xl) var(--spacing-lg) var(--spacing-xl);
                    color: var(--text-dark);
                }

                .premises-info-banner i {
                    color: var(--admin-primary);
                    font-size: 1.2rem;
                }

                .schedule-controls {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-md);
                }

                .current-week {
                    font-weight: 600;
                    color: var(--text-dark);
                }

                .schedule-grid {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    gap: 1px;
                    background: rgba(0, 0, 0, 0.05);
                }

                .day-column {
                    background: var(--white);
                    min-height: 300px;
                    transition: all 0.3s ease;
                }

                .day-column.off-day {
                    background: #f9fafb;
                    opacity: 0.7;
                }

                .day-header {
                    background: var(--admin-primary);
                    color: white;
                    padding: var(--spacing-md);
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-xs);
                }

                .day-column.off-day .day-header {
                    background: #6b7280;
                }

                .day-header h4 {
                    margin: 0;
                    font-size: 0.9rem;
                    font-weight: 600;
                }

                .day-date {
                    font-size: 0.8rem;
                    opacity: 0.9;
                }

                .off-day-badge {
                    background: rgba(255, 255, 255, 0.2);
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-size: 0.7rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.3rem;
                    align-self: center;
                }

                .off-day-badge i {
                    font-size: 0.65rem;
                }

                .day-shifts {
                    padding: var(--spacing-sm);
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-xs);
                }

                .schedule-shift-item {
                    background: rgba(var(--admin-primary-rgb), 0.05);
                    border-left: 3px solid var(--admin-primary);
                    padding: var(--spacing-sm);
                    border-radius: var(--radius-sm);
                }

                .shift-time {
                    font-size: 0.75rem;
                    color: var(--admin-primary);
                    font-weight: 600;
                    margin-bottom: var(--spacing-xs);
                }

                .shift-name {
                    font-size: 0.8rem;
                    color: var(--text-dark);
                    font-weight: 500;
                    margin-bottom: var(--spacing-xs);
                }

                .shift-staff-count {
                    font-size: 0.7rem;
                    color: var(--text-light);
                }

                .off-day-message {
                    text-align: center;
                    padding: var(--spacing-lg);
                    color: #6b7280;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: var(--spacing-sm);
                }

                .off-day-message i {
                    font-size: 2rem;
                    opacity: 0.5;
                }

                .off-day-message span {
                    font-size: 0.85rem;
                    font-weight: 500;
                }

                .no-shifts-message {
                    text-align: center;
                    padding: var(--spacing-md);
                    color: var(--text-light);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: var(--spacing-xs);
                }

                .no-shifts-message i {
                    font-size: 1.5rem;
                    opacity: 0.5;
                }

                .no-shifts-message span {
                    font-size: 0.8rem;
                }

                .empty-state {
                    text-align: center;
                    padding: var(--spacing-xl) * 2;
                    color: var(--text-light);
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                }

                .empty-state i {
                    font-size: 3rem;
                    margin-bottom: var(--spacing-lg);
                    color: var(--admin-primary);
                    opacity: 0.5;
                }

                .empty-state h3 {
                    margin-bottom: var(--spacing-sm);
                    color: var(--text-dark);
                }

                /* Modal Styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: var(--spacing-md);
                }

                .assign-modal {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-lg);
                    width: 100%;
                    max-width: 500px;
                    max-height: 90vh;
                    overflow: hidden;
                }

                .history-modal {
                    max-width: 720px;
                }

                .history-modal .modal-body {
                    max-height: 65vh;
                    overflow-y: auto;
                }

                .history-staff-code {
                    font-weight: 400;
                    color: var(--text-light);
                    font-size: 0.9rem;
                }

                .assignment-actions {
                    display: flex;
                    gap: 0.4rem;
                }

                .status-badge-active,
                .status-badge-ended {
                    display: inline-block;
                    padding: 2px 10px;
                    border-radius: 999px;
                    font-size: 0.8rem;
                    font-weight: 600;
                }

                .status-badge-active {
                    background: #e7f8ee;
                    color: #1a7f4e;
                }

                .status-badge-ended {
                    background: #f1f2f4;
                    color: #666;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: var(--spacing-xl);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                    background: var(--light-color);
                }

                .modal-header h3 {
                    margin: 0;
                    color: var(--text-dark);
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 1.2rem;
                    cursor: pointer;
                    color: var(--text-light);
                    padding: var(--spacing-xs);
                }

                .modal-body {
                    padding: var(--spacing-xl);
                }

                .modal-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: var(--spacing-sm);
                    padding: var(--spacing-xl);
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                    background: var(--light-color);
                }

                .btn {
                    padding: var(--spacing-sm) var(--spacing-md);
                    border: none;
                    border-radius: var(--radius-md);
                    font-size: 0.85rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: var(--spacing-xs);
                    text-decoration: none;
                }

                .btn-primary {
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                    color: white;
                    box-shadow: 0 5px 15px rgba(99, 102, 241, 0.3);
                }

                .btn-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.4);
                }

                .btn-secondary {
                    background: transparent;
                    color: #718096;
                    border: 2px solid #e2e8f0;
                    transition: all 0.3s ease;
                }

                .btn-secondary:hover {
                    border-color: #6366f1;
                    color: #6366f1;
                    background: rgba(99, 102, 241, 0.05);
                }

                .btn-danger {
                    background: var(--error);
                    color: white;
                }

                .btn-danger:hover {
                    background: #c53030;
                }

                .btn-sm {
                    padding: var(--spacing-xs) var(--spacing-sm);
                    font-size: 0.8rem;
                }

                .btn-action {
                    padding: var(--spacing-md) var(--spacing-lg);
                    font-size: 0.95rem;
                    min-width: 140px;
                    font-weight: 600;
                }

                @media (max-width: 768px) {
                    .page-header {
                        flex-direction: column;
                        gap: 1.5rem;
                        text-align: center;
                    }

                    .header-right {
                        flex-direction: column;
                        width: 100%;
                    }

                    .filters-grid {
                        grid-template-columns: 1fr;
                    }

                    .shifts-grid {
                        grid-template-columns: 1fr;
                    }

                    .shift-actions {
                        flex-direction: column;
                    }

                    .tab-navigation {
                        flex-direction: column;
                    }

                    .tab-btn {
                        border-bottom: none;
                        border-right: 3px solid transparent;
                    }

                    .tab-btn.active {
                        border-bottom-color: transparent;
                        border-right-color: var(--admin-primary);
                    }

                    .schedule-header {
                        flex-direction: column;
                        gap: var(--spacing-md);
                        align-items: flex-start;
                    }

                    .schedule-header-left {
                        width: 100%;
                    }

                    .premises-selector {
                        flex-direction: column;
                        align-items: flex-start;
                        width: 100%;
                    }

                    .premises-selector select {
                        width: 100%;
                    }

                    .schedule-controls {
                        width: 100%;
                        justify-content: center;
                    }

                    .premises-info-banner {
                        margin: 0 var(--spacing-md) var(--spacing-md) var(--spacing-md);
                        font-size: 0.85rem;
                    }

                    .schedule-grid {
                        grid-template-columns: 1fr;
                    }

                    .day-column {
                        min-height: 200px;
                    }

                    .assignments-table {
                        font-size: 0.8rem;
                    }

                    .assignments-table th,
                    .assignments-table td {
                        padding: var(--spacing-sm);
                    }

                    .working-days {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .search-input {
                        font-size: 0.85rem;
                        padding: 0.65rem 1rem 0.65rem 2.5rem;
                    }

                    .search-results-info {
                        font-size: 0.85rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default ShiftManagement;
